import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import { Fraction } from '@learnforge/demo-math/numeric';
import { lcm } from '../arith/factorisation';
import { createFractionBar, type FractionBarHandle } from '../render/fraction-bar';
import { createSvgEl } from '@learnforge/demo-render';
import type { ArithContext, ArithKey } from '../types';
import { defineExperiment, type CustomExperimentSpec } from '@learnforge/demo-render/harness';

export interface DenFrame {
  num: number;
  den: number;
}

// Rewrites a and b over their common denominator WITHOUT simplifying (Fraction
// would auto-reduce 3/6 back to 1/2, hiding the point). Returns raw num/den frames.
export function commonDenominatorFrames(a: Fraction, b: Fraction): DenFrame[] {
  if (a.den === b.den) return [{ num: a.num, den: a.den }];
  const common = lcm(a.den, b.den);
  return [
    { num: a.num, den: a.den },
    { num: (a.num * common) / a.den, den: common },
    { num: (b.num * common) / b.den, den: common },
  ];
}

type Op = '+' | '-' | '*' | '/';

const OP_SYMBOL: Record<Op, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };

function applyOp(a: Fraction, b: Fraction, op: Op): Fraction {
  switch (op) {
    case '+':
      return a.add(b);
    case '-':
      return a.sub(b);
    case '*':
      return a.mul(b);
    case '/':
      return a.div(b);
  }
}

const BAR_X = 110;
const BAR_W = 670;
const REGION_TOP = 30;
const REGION_BOTTOM = 350;
const TARGET_ROW_H = 46; // comfortable height for one whole
const BAR_GAP = 22;

// A fraction's whole-count: how many stacked rows it needs (1 for proper).
function wholesOf(f: Fraction): number {
  return Math.max(1, Math.ceil(Math.abs(f.num) / f.den));
}

interface State {
  a: Fraction;
  b: Fraction;
  op: Op;
  advanced: boolean;
}

const ADVANCED_STORAGE_KEY = 'arithmetic.fraction-foundry.advanced';

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.fraction-foundry.label',
    demoId: 'arithmetic',
    advancedKey: 'fraction-foundry',

    initialState(ctx): State {
      return {
        a: new Fraction(1, 2),
        b: new Fraction(1, 3),
        op: '+',
        advanced: ctx.advanced,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 400', class: 'arith-stage' });
      host.appendChild(svg);

      function makeSideLabel(): SVGTextElement {
        const t = createSvgEl('text', {
          class: 'arith-bar-side-label',
          x: 20,
          'dominant-baseline': 'central',
        });
        svg.appendChild(t);
        return t;
      }
      const labelA = makeSideLabel();
      const labelB = makeSideLabel();
      const labelR = makeSideLabel();

      const aGroup = createSvgEl('g', {});
      const bGroup = createSvgEl('g', {});
      const rGroup = createSvgEl('g', {});
      svg.appendChild(aGroup);
      svg.appendChild(bGroup);
      svg.appendChild(rGroup);

      const formula = createSvgEl('text', {
        class: 'arith-readout',
        x: 400,
        y: 380,
        'text-anchor': 'middle',
      });
      svg.appendChild(formula);

      // Transient closure vars — NOT in State
      let barHandles: FractionBarHandle[] = [];

      // ── Panel: op-selector + (advanced) fraction inputs + advanced toggle; built ONCE ──
      const OPS: Op[] = ['+', '-', '*', '/'];
      const opRow = document.createElement('div');
      opRow.className = 'arith-op-row';
      const opButtons = new Map<Op, HTMLButtonElement>();
      for (const op of OPS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'arith-op-btn';
        btn.textContent = OP_SYMBOL[op];
        btn.addEventListener('click', () => {
          controls.setState((s) => ({ ...s, op }));
        });
        opButtons.set(op, btn);
        opRow.appendChild(btn);
      }

      // Advanced fraction inputs (created once, inserted/removed by update())
      let inputA: HTMLInputElement | null = null;
      let inputB: HTMLInputElement | null = null;

      function makeFractionInput(which: 'a' | 'b'): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-frac-input';
        const label = document.createElement('label');
        label.htmlFor = `fraction-foundry-${which}`;
        label.textContent = which.toUpperCase();
        const input = document.createElement('input');
        input.id = `fraction-foundry-${which}`;
        input.type = 'text';
        input.addEventListener('change', () => {
          try {
            const parsed = Fraction.parse(input.value);
            input.classList.remove('invalid');
            controls.setState((s) => ({ ...s, [which]: parsed }));
          } catch {
            input.classList.add('invalid');
          }
        });
        wrap.appendChild(label);
        wrap.appendChild(input);
        if (which === 'a') inputA = input;
        else inputB = input;
        return wrap;
      }

      const inputAWrap = makeFractionInput('a');
      const inputBWrap = makeFractionInput('b');

      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced',
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_STORAGE_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      // Append permanent panel children; advanced inputs inserted/removed in update()
      ctx.panel.appendChild(opRow);
      ctx.panel.appendChild(advToggle.element);

      // Track whether advanced inputs are currently in the panel
      let inputsInPanel = false;

      // ── RenderHandle ──
      function update(state: State): void {
        const { a, b, op, advanced } = state;

        // Bulk-destroy previous bar handles and recreate
        for (const h of barHandles) h.destroy();
        barHandles = [];

        let result: Fraction | null = null;
        try {
          result = applyOp(a, b, op);
        } catch {
          result = null;
        }

        const specs = [
          { group: aGroup, label: labelA, frac: a, maxDen: advanced ? 24 : 12 },
          { group: bGroup, label: labelB, frac: b, maxDen: advanced ? 24 : 12 },
        ];
        if (result !== null) {
          specs.push({ group: rGroup, label: labelR, frac: result, maxDen: advanced ? 48 : 24 });
          rGroup.style.display = '';
        } else {
          rGroup.style.display = 'none';
          labelR.textContent = '';
        }

        // Each whole gets a comfortable row height; only shrink when many wholes
        // would overflow the region. Bars can therefore differ in height.
        const wholes = specs.map((s) => wholesOf(s.frac));
        const totalWholes = Math.max(
          1,
          wholes.reduce((sum, w) => sum + w, 0),
        );
        const gapsTotal = (specs.length - 1) * BAR_GAP;
        const budget = REGION_BOTTOM - REGION_TOP;
        const rowH = Math.min(TARGET_ROW_H, (budget - gapsTotal) / totalWholes);
        const usedHeight = totalWholes * rowH + gapsTotal;
        let cursor = REGION_TOP + (budget - usedHeight) / 2;

        specs.forEach((s, idx) => {
          const barH = (wholes[idx] ?? 1) * rowH;
          s.group.setAttribute('transform', `translate(${BAR_X}, ${cursor})`);
          s.label.setAttribute('y', String(cursor + barH / 2));
          s.label.textContent = s.frac.toString();
          barHandles.push(
            createFractionBar(s.group, {
              fraction: s.frac,
              maxDenominator: s.maxDen,
              editable: false,
              showSubdivisions: true,
              highlightUnit: null,
              showLabel: false,
              width: BAR_W,
              height: barH,
            }),
          );
          cursor += barH + BAR_GAP;
        });

        formula.textContent =
          result !== null
            ? `${a.toString()} ${OP_SYMBOL[op]} ${b.toString()} = ${result.toString()}`
            : `${a.toString()} ${OP_SYMBOL[op]} ${b.toString()} = ✗`;

        // Reflect the active op button
        for (const [o, btn] of opButtons) {
          btn.classList.toggle('active', o === op);
        }

        // Show/hide advanced inputs by inserting/removing from the panel
        if (advanced && !inputsInPanel) {
          ctx.panel.insertBefore(inputAWrap, opRow);
          ctx.panel.insertBefore(inputBWrap, advToggle.element);
          inputsInPanel = true;
        } else if (!advanced && inputsInPanel) {
          ctx.panel.removeChild(inputAWrap);
          ctx.panel.removeChild(inputBWrap);
          inputsInPanel = false;
        }
        // Guarded value sync (only when inputs are present and not focused)
        if (advanced) {
          if (inputA !== null && inputA !== document.activeElement) {
            const display = a.toString();
            if (inputA.value !== display) inputA.value = display;
          }
          if (inputB !== null && inputB !== document.activeElement) {
            const display = b.toString();
            if (inputB.value !== display) inputB.value = display;
          }
        }
      }

      return {
        update,
        dispose() {
          for (const h of barHandles) h.destroy();
          barHandles = [];
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
export const mountFractionFoundry = experiment.mount.bind(experiment);
